/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import googleMaps from '../util/google-maps.js';
import _ from "underscore";
import util from "../util/util.js";
import geo from "../util/geo.js";
import validate from "../util/validate.js";
import jobQueue from "../util/job-queue.js";

import sharedSitesMap from "../map/shared-sites.js";
import areasMap from "../map/areas.js";
import mapStyles from "../map/styles";

import markerLayer from "../map/marker-layer.js";
import UTMGridMapType from "../map/utm-grid-layer.js";
import * as utmGrid from "../map/utm-grid.js";

import { asyncComputed } from "../util/ko-util.js";

import "./shared/coordinate-entry.js";
import "./shared/location-search.js";

ko.bindingHandlers.googleMap = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    if(bindingContext.$data.initMap) {
      googleMaps.load().then(function() {
        var map = bindingContext.$data.initMap(element);
        valueAccessor()(map);
      });
    }
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    // if(val) console.warn("googleMap binding is read-only; ignoring update: " + val);
  }
};

function ll(latLng) {
  if(typeof latLng.lat == "function") return latLng;
  if(latLng.lon) return new google.maps.LatLng(latLng.lat, latLng.lon);
  if(latLng.lng) return new google.maps.LatLng(latLng.lat, latLng.lng);
}

var SurveyLocationViewModel = function(params) {
	var self = this;

  self.survey = params.parent.survey;
  self.showNextTab = params.parent.showNextTab;

  self.survey.locationViewModel = self;

  self.map = ko.observable();

  self.lat = ko.observable(-22.50831);
  self.lng = ko.observable(136.078612);

  self.markers = ko.observableArray([]);

  self.zoom = ko.observable(-1);

  self.relocateSurveyPointActive = ko.observable(false);

  // Site-based entry: surveys are at existing sites only, and the survey point is inferred from that existing site
  self.siteBasedEntry = ko.pureComputed(function() {
    return self.survey.program().siteBasedEntry;
  });

  // Route-based entry: survey has a start and finish location and may be one of:
  // a) Existing route: full survey
  // b) Existing route: partial survey
  // c) Ad-hoc route
  self.routeBasedEntry = ko.pureComputed(function() {
    return self.survey.program().routeBasedEntry;
  });

  self.existingRoutesDefinedBySharedSites = ko.pureComputed(function() {
    return self.survey.program().existingRoutesDefinedBySharedSites;
  });

  self.showSurveyPointOverlay = ko.pureComputed(function() {
    return !self.routeBasedEntry() && !self.siteBasedEntry() && !self.relocateSurveyPointActive() && !self.survey.program().disableSurveyPointDetailEditing;
  });
  self.showOptionsOverlay = ko.pureComputed(function() {
    return !self.routeBasedEntry() && !self.siteBasedEntry() && !self.relocateSurveyPointActive();
  });
  self.showSearchOverlay = ko.pureComputed(function() {
    return !(self.routeBasedEntry()  && self.existingRoutesDefinedBySharedSites()) && !self.relocateSurveyPointActive();
  })
  self.showRouteOverlay = self.routeBasedEntry;

  // kick-off requests now
  self.sharedSitesPromise = birdata.sharedSites();
  self.areasPromise = birdata.areas();

  self.sharedSites = ko.observable([]);
  self.sharedSitesPromise.then(self.sharedSites);

  self.areas = ko.observable([]);
  self.areasPromise.then(self.areas);

  // Site menu (note there is a seperate menu system for routes)

  self.siteMenuOverlayStyle = ko.pureComputed(function() {
    let programCode = self.survey.program().code;
    switch(programCode) {
    case 'swp':
      return 'region-locality-site';
    case 'bof', 'obp':
      return 'site-only';
    default:
      return null;
    }
  });

  self.showSiteMenuOverlay = ko.pureComputed(function() {
    return self.siteMenuOverlayStyle() != null;
  });

  self.showSiteOverlay = ko.pureComputed(function() {
    return self.survey.program().code == 'egnb';
  })

  self.siteMenu = (function() {
    var menu = {};

    menu.selectedRegion = ko.observable();
    menu.selectedLocality = ko.observable();
    menu.selectedSite = self.survey.sharedSite;

    menu.showRegionMenu = ko.pureComputed(function() {
      return self.siteMenuOverlayStyle() == 'region-locality-site';
    });

    menu.showLocalityMenu = ko.pureComputed(function() {
      return menu.selectedRegion() && self.siteMenuOverlayStyle() == 'region-locality-site';
    });

    menu.showSiteMenu = ko.pureComputed(function() {
      let style = self.siteMenuOverlayStyle();
      if(style == 'region-locality-site') {
        return menu.selectedRegion() && menu.selectedLocality();
      } else if(style == 'site-only') {
        return true;
      }
    });

    var sites = ko.pureComputed(function() {
      if(self.siteBasedEntry()) {
        let filterFn = siteFilterFn();
        return self.sharedSites().filter(filterFn);
      } else {
        return [];
      }
    });

    menu.regions = ko.pureComputed(function() {
      return util.uniq(sites().map(function(x) { return x.metadata && x.metadata.region; })).sort();
    });

    menu.localities = ko.pureComputed(function() {
      var region = menu.selectedRegion();
      if(region == null) {
        return null;
      } else {
        return util.uniq(sites()
          .filter(function(x) { return x.metadata && x.metadata.region == region; })
          .map(function(x) { return x.metadata && x.metadata.locality; }))
          .sort();
      }
    });

    menu.sites = ko.pureComputed(function() {
      let style = self.siteMenuOverlayStyle();
      if(style == 'region-locality-site') {
        var region = menu.selectedRegion();
        var locality = menu.selectedLocality();
        if(region == null || locality == null) {
          return [];
        } else {
          return sites().filter(function(x) { return x.metadata && x.metadata.locality == locality; }).sort();
        }
      } else if(style == 'site-only') {
        return sites();
      }
    });

    menu.showNextButton = ko.pureComputed(function() { return menu.selectedSite() != null; });

    menu.selectedSite.subscribe(function(site) {
      if(site) {
        zoomToSharedSite(site);
        setSurveyPointToSiteCentroid(site);
      } else {
        clearSurveyPoint();
      }
    });

    menu.setSite = function(site, assumeSharedSitesLoaded) {
      if(!assumeSharedSitesLoaded) {
        self.sharedSitesPromise.then(() => menu.setSite(site, true));
        return;
      }

      var site = site && sharedSiteById(site.id);

      function update(obs, x) {
        if(obs() !== x) { obs(x); }
      }

      if(site && site.metadata) {
        update(menu.selectedRegion, site.metadata.region);
        update(menu.selectedLocality, site.metadata.locality);
        update(menu.selectedSite, site);
      }

      // A bit hacky - make sure the selectable site list includes the selected site
      if(self.selectedSiteList().length == 0) {
        self.selectedSiteList([site]);
      }
    };

    return menu;
  })();

  // Site based entry only ------

  // List of possible sites based on selected site marker
  self.selectedSiteList = ko.observable([]);
  // Actual selected site
  self.selectedSite = self.survey.sharedSite;

  // End site based entry only ------

  function setSurveyPointToSiteCentroid(site) {
    var sp = self.survey.surveyPoint();
    if(sp && sp.sharedSite && sp.sharedSite.id == site.id) {
      return;
    }

    var surveyPoint = { ...site.centroid };
    surveyPoint.sharedSite = site;
    surveyPoint.name = ko.observable(site.name);
    surveyPoint.accuracy = ko.observable(0);
    updateSurveyPoint(surveyPoint, false);
  }

  // Start route-based selection. Most programs ignore this entirely and use survey points only.

  self.routeType = ko.observable('existing'); // existing, ad-hoc
  self.selectedRegion = ko.observable(null);
  self.selectedRoute = ko.observable(null);
  self.completeRouteSurveyed = ko.observable(null);
  self.startPoint = ko.observable(null);
  self.finishPoint = ko.observable(null);
  self.coordinateFormat = ko.observable();
  self.routeEndpointsRequired = ko.pureComputed(function() {
    return !self.existingRoutesDefinedBySharedSites() || self.routeType() == 'ad-hoc' || (self.routeType() == 'existing' && self.completeRouteSurveyed() === false);
  });
  self.showEndpoints = ko.pureComputed(function() {
    return self.routeBasedEntry() && self.routeEndpointsRequired();
  });
  self.mapClickWillSetRouteStart = ko.pureComputed(function() {
    return !self.startPoint();
  });
  self.mapClickWillSetRouteFinish = ko.pureComputed(function() {
    return self.startPoint() && !self.finishPoint();
  });

  self.routeDistance = ko.pureComputed(function() {
    var a = self.startPoint(), b = self.finishPoint();
    if(a && b && self.map()) {
      try {
        return google.maps.geometry.spherical.computeDistanceBetween(ll(a), ll(b));
      } catch(e) {
        // Typically because google maps isn't yet initialised - don't care
        console.log(e);
      }
    }
  });

  self.routeDistanceText = ko.pureComputed(function() {
    return self.routeDistance() ?
      "Straight line distance: " + util.formatDistance(self.routeDistance()) :
      "";
  });

  self.regions = ko.pureComputed(function() {
    return util.uniq(
      self.sharedSites()
        .filter(function(x) { return x.layer == 'bnb' })
        .map(function(x) { return x.metadata.region; })
    ).sort();
  });

  self.routes = ko.pureComputed(function() {
    var region = self.selectedRegion();
    if(region) {
      return _.sortBy(self.sharedSites().filter(function(site) {
        return site.metadata && site.metadata.region == region && site.layer == 'bnb';
      }), 'name');
    } else {
      return [];
    }
  });

  self.routeId = ko.pureComputed(function() {
    var r = self.selectedRoute();
    return r && r.id;
  });

  self.routePolygon = asyncComputed(function() {
    var id = self.routeId();
    if(id) {
      return birdata.sharedSite(id).then(function(x) { return x.geom; });
    }
  });

  self.endpointsWithinRoute = ko.pureComputed(function() {
    var a = self.startPoint(), b = self.finishPoint(), r = self.routePolygon();
    if(a && b && r && self.map()) {
      return geo.containsPoint(r, a.lat, a.lon) && geo.containsPoint(r, b.lat, b.lon);
    }
  });

  self.survey.routeValidations = [
    validate(self.selectedRoute, function(x) {
      if(self.routeBasedEntry() && self.existingRoutesDefinedBySharedSites() && self.routeType() == 'existing' && !x) {
        return "Route is required";
      }
    }, { tab: 'location' }),
    // validate(self.routeDistance, function(x) {
    //   if(x > 100000) {
    //     return "Route distance must not be greater than 100 km";
    //   }
    // }, { tab: 'location' }),
    validate(self.completeRouteSurveyed, function(x) {
      if(x === null && self.routeType() == 'existing' && self.selectedRoute()) {
        return "Must specify whether complete route was specified";
      }
    }, { tab: 'location' }),
    validate(self.startPoint, function(x) {
      if(self.routeBasedEntry() && self.routeEndpointsRequired() && !x) {
        return "Route start point is required";
      }
    }, { tab: 'location' }),
    validate(self.finishPoint, function(x) {
      if(self.routeBasedEntry() && self.routeEndpointsRequired() && !x) {
        return "Route finish point is required";
      }
    }, { tab: 'location' }),
    validate(self.endpointsWithinRoute, function(x) {
      if(self.routeBasedEntry() && self.routeEndpointsRequired() && x === false) {
        return "Start and finish point must lie within route boundary";
      }
    }, { tab: 'location' })
  ];

  self.routeOK = ko.pureComputed(function() {
    return _.all(self.survey.routeValidations, function(v) { return !v.errorInfo(); });
  });

  self.routeError = ko.pureComputed(function() {
    var v = _.find(self.survey.routeValidations, function(v) { return v.error(); });
    if(v) {
      return v.error();
    }
  });

  self.showRouteLocationName = ko.pureComputed(function() {
    return self.routeType() == 'ad-hoc' && self.existingRoutesDefinedBySharedSites() && self.survey.surveyPoint() && self.startPoint() && self.finishPoint() && !self.routeError()
  })

  ko.computed(function() {
    self.survey.routeInfo({
      start: self.routeEndpointsRequired() ? self.startPoint() : undefined,
      finish: self.routeEndpointsRequired() ? self.finishPoint() : undefined,
      sharedSite: self.routeType() == 'existing' ? self.selectedRoute() : undefined,
      partialRoute: self.routeType() == 'existing' ? !self.completeRouteSurveyed() : false,
      type: self.routeType()
    });

    var surveyPoint;

    if(self.routeEndpointsRequired() && self.startPoint()) {
      surveyPoint = { ...self.startPoint() };
      surveyPoint.name = ko.observable("");
    } else if(self.selectedRoute()) {
      surveyPoint = { ...self.selectedRoute().centroid };
      surveyPoint.name = ko.observable(self.selectedRoute().name);
    }

    if(surveyPoint) {
      surveyPoint.accuracy = ko.observable(0);
      ko.ignoreDependencies(function() {
        updateSurveyPoint(surveyPoint, false);
      });
    }
  });

  // End route-based selection

  self.tabVisible = ko.pureComputed(function() {
    var section = params.parent.currentSection(); // listen to observable
    return section.component.name == 'survey-edit-location';
  });

  self.showAccuracyField = ko.pureComputed(function() {
    var s = self.survey.surveyPoint();
    return s && !s.id;// && s.sharedSites() && s.sharedSites().length == 0;
  });

  self.canCreateMarkerByTap = ko.pureComputed(function() {
    return self.zoom() > 13 && self.zoom() != -1;
  });

  self.status = ko.computed(function() {
    if(self.zoom() == -1) {
      return "";
    } else if(self.canCreateMarkerByTap()) {
      return "Tap anywhere on the map to create a new survey point or click an existing survey point";
    } else {
      return "Zoom in to create a new survey point or click an existing survey point";
    }
  });

  self.surveyPointSelected = ko.pureComputed(function() {
    return !!self.survey.surveyPoint();
  });

  self.surveyPointError = ko.pureComputed(function() {
    var program = self.survey.program();
    var surveyPoint = self.survey.surveyPoint();

    if(self.survey.currentProgramData().surveyPointError) {
      return self.survey.currentProgramData().surveyPointError();
    }

    if(program && program.validateSurveyPoint && surveyPoint && surveyPoint.sharedSites()) {
      return program.validateSurveyPoint(surveyPoint);
    } else {
      return null;
    }
  });

  self.newSurveyPointSelected = ko.pureComputed(function() {
    return self.surveyPointSelected() && !self.existingSurveyPointSelected();
  });

  self.existingSurveyPointSelected = ko.pureComputed(function() {
    return self.survey.surveyPoint() && self.survey.surveyPoint().id;
  });

  self.enableNextButton = ko.pureComputed(function() {
    var s = self.survey.surveyPoint();
    return s && (s.id || s.name.valid()) && !self.surveyPointError();
  });

  self.surveyCountFormatted = ko.pureComputed(function() {
    var s = self.survey.surveyPoint();
    if(s && s.survey_count != undefined) {
      return util.formatQuantity(s.survey_count, "existing survey");
    } else {
      return "";
    }
  });

  self.accuracyOptions = [
  {
    metres: 0,
    value: "Within survey area"
  },
  {
    metres: 200,
    value: "Within 200 m of survey area"
  },
  {
    metres: 500,
    value: "Within 500 m of survey area"
  },
  {
    metres: 1000,
    value: "Within 1 km of survey area"
  },
  {
    metres: 100000000,
    value: "> 1 km from survey area"
  },
  ];

  // Start manual lat lon

  self.showManualCoordinateEntry = ko.observable(false);
  self.showDecimalDegrees = ko.observable(false);
  self.manualLat = ko.observable(null);
  self.manualLon = ko.observable(null);
  self.manualLatDms = observableDms(self.manualLat);
  self.manualLonDms = observableDms(self.manualLon);
  self.manualLatNS = ko.observable('S');
  self.manualLonEW = ko.observable('E');

  self.manualCoordinatesValid = ko.pureComputed(function() {
    if(self.showDecimalDegrees()) {
      return isNumberInRange(self.manualLat(), 0, 90) &&
      isNumberInRange(self.manualLon(), 0, 180);
    } else {
      return isNumberInRange(self.manualLatDms.d(), 0, 90) &&
      isNumberInRange(self.manualLatDms.m(), 0, 60) &&
      isNumberInRange(self.manualLatDms.s(), 0, 60) &&
      isNumberInRange(self.manualLonDms.d(), 0, 180) &&
      isNumberInRange(self.manualLonDms.m(), 0, 60) &&
      isNumberInRange(self.manualLonDms.s(), 0, 60);
    }
  });

  self.manualCoordinatesValidationMessage = ko.pureComputed(function() {
    if(self.showDecimalDegrees()) {
      return inRangeValidationMessage(self.manualLat(), 0, 90, 'Latitude') ||
        inRangeValidationMessage(self.manualLon(), 0, 180, 'Longitude');
    } else {
      return dmsValidationMessage(self.manualLatDms, 90, 'latitude') ||
        dmsValidationMessage(self.manualLonDms, 180, 'longitude');
    }
  });

  function dmsValidationMessage(dms, maxDegrees, label) {
      return inRangeValidationMessage(dms.d(), 0, maxDegrees, 'Degrees ' + label) ||
      inRangeValidationMessage(dms.m(), 0, 60, 'Minutes ' + label) ||
      inRangeValidationMessage(dms.s(), 0, 60, 'Seconds ' + label);
  }

  function inRangeValidationMessage(x, min, max, label) {
    if(x.trim() != '' && !isNumberInRange(x, min, max)) {
      return label + " must be between " + min + " and " + max;
    }
  }

  self.clickedManualEntry = function() {
    resetManualCoordinates();
    self.showManualCoordinateEntry(true);
  };

  self.completeManualCoordinateEntry = function() {
    self.showManualCoordinateEntry(false);
    var lat, lon;

    if(self.showDecimalDegrees()) {
      lat = +self.manualLat();
      lon = +self.manualLon();
    } else {
      lat = self.manualLatDms.val();
      lon = self.manualLonDms.val();
    }

    if(self.manualLatNS() == 'S') lat = -lat;
    if(self.manualLonEW() == 'W') lon = -lon;

    if(self.relocateSurveyPointActive()) {
      self.selectSurveyPointRelocationDestination({ lat, lon });
    } else {
      updateSurveyPoint({ lat, lon }, true);
    }
  };

  self.cancelManualCoordinateEntry = function() {
    self.showManualCoordinateEntry(false);
  };

  function resetManualCoordinates() {
    _.each([self.manualLat, self.manualLon,
      self.manualLatDms.d, self.manualLatDms.m, self.manualLatDms.s,
      self.manualLonDms.d, self.manualLonDms.m, self.manualLonDms.s], function(o) {
        o("");
      });
    self.manualLatNS('S');
    self.manualLonEW('E');
  }

  function isNumberInRange(x, min, max) {
    return _.isFinite(x) && x >= min && x <= max;
  }

  function fromDms(d, m, s) {
    return (+d) + (m / 60) + (s / 3600);
  }

  function observableDms(degrees) {
    var dms = {
      d: ko.observable(),
      m: ko.observable(),
      s: ko.observable()
    };

    dms.val = function() {
      return fromDms(dms.d(), dms.m(), dms.s());
    };

    return dms;
  }

  ko.computed(function() {
    var program = self.survey.program();
    if(program.code == 'segbc') {
      self.showDecimalDegrees(true);
    }
  });

  // end manual lat lon

  self.enableSurveyPointEditing = ko.observable(true);
  // params.parent.mePromise.then(function(user) {
  //   if(user && user.is_administrator) {
  //     self.enableSurveyPointEditing(true);
  //   }
  // })

  // Start survey point rename

  self.showRenameSurveyPointDialog = ko.observable(false);
  self.surveyPointNewName = ko.observable("");
  self.renameSurveyPointStatus = ko.observable("idle");
  self.renameSurveyPointError = ko.observable(null);

  self.renameSurveyPoint = function() {
    self.surveyPointNewName(ko.unwrap(self.survey.surveyPoint().name));
    self.showRenameSurveyPointDialog(true);
  }

  self.cancelRenameSurveyPoint = function() {
    self.showRenameSurveyPointDialog(false);
  }

  self.updateSurveyPointName = function() {
    var sp = self.survey.surveyPoint();
    var newName = self.surveyPointNewName().trim();
    self.renameSurveyPointStatus("updating");
    self.renameSurveyPointError(null);

    birdata.updateSurveyPoint(sp.id, { name: newName }).then(function() {
      self.showRenameSurveyPointDialog(false);
      self.renameSurveyPointStatus("idle");
      sp.name(newName);
      // self.survey.surveyPoint(sp);
      updateSurveyPoint(sp, true);
      self.refreshSurveyPoints(true);
    }).catch(function(error) {
      console.log(error);
      self.renameSurveyPointStatus("error");
      self.renameSurveyPointError("Failed to rename survey point");
    });
  }

  self.renameSurveyPointEnabled = ko.pureComputed(function() {
    return self.renameSurveyPointStatus() != "updating" && self.surveyPointNewName().trim() != "";
  });

  // End survey point rename

  // Start survey point relocate

  // Valid states:
  // idle,
  // precheck-loading, precheck-loaded, precheck-error,
  // selecting,
  // postcheck-loading, postcheck-loaded, postcheck-error,
  // updating, update-error
  self.relocateSurveyPointStatus = ko.observable("idle");

  self.relocateSurveyPointCheck = ko.observable(null);
  self.relocateSurveyPointCheckConfirmed = ko.observable(false);
  self.surveyPointRelocationDestination = ko.observable(null);
  self.surveyPointName = ko.pureComputed(function() {
    return ko.unwrap(self.survey.surveyPoint().name);
  });

  self.relocateSurveyPointStatus.subscribe(function(status) {
    self.relocateSurveyPointActive(status != 'idle');
  });

  self.showSurveyPointRelocationPreCheckDialog = ko.pureComputed(function() {
    return self.relocateSurveyPointStatus().match('precheck');
  });

  self.showSurveyPointRelocationPostCheckDialog = ko.pureComputed(function() {
    return self.relocateSurveyPointStatus().match('postcheck|updating|update-error');
  });

  self.relocateSurveyPoint = function() {
    self.relocateSurveyPointCheckConfirmed(false);

    var sp = self.survey.surveyPoint();
    self.relocateSurveyPointStatus('precheck-loading');
    birdata.surveyPointRelocationCheck(sp.id).then(function(checkData) {
      self.relocateSurveyPointCheck(checkData);
      self.relocateSurveyPointStatus('precheck-loaded');
    }).catch(function() {
      self.relocateSurveyPointStatus('precheck-error');
    });
  }

  self.cancelRelocateSurveyPoint = function() {
    self.relocateSurveyPointStatus('idle');
  }

  self.cancelRelocateSurveyPointPostCheck = function() {
    self.relocateSurveyPointStatus('selecting');
  }

  self.preConfirmRelocateSurveyPoint = function() {
    self.relocateSurveyPointStatus('selecting');
  }

  self.selectSurveyPointRelocationDestination = function(relocationInfo) {
    var sp = self.survey.surveyPoint();
    self.surveyPointRelocationDestination(relocationInfo);
    self.relocateSurveyPointStatus('postcheck-loading');

    var checkParams = relocationInfo.lat ? {
      lat: relocationInfo.lat,
      lon: relocationInfo.lon
    } : {
      dest_id: relocationInfo.id
    };

    birdata.surveyPointRelocationCheck(sp.id, checkParams).then(function(checkData) {
      self.relocateSurveyPointCheck(checkData);
      self.relocateSurveyPointStatus('postcheck-loaded');
    }).catch(function() {
      self.relocateSurveyPointStatus('postcheck-error');
    })
  }

  self.surveyPointRelocationDestinationDescription = function() {
    var dest = self.surveyPointRelocationDestination();

    if(dest.name) {
      return ko.unwrap(dest.name);
    } else {
      return dest.lat.toFixed(8) + ", " + dest.lon.toFixed(8);
    }
  }

  self.completeRelocateSurveyPoint = function() {
    var relocationInfo = self.surveyPointRelocationDestination();
    var sp = self.survey.surveyPoint();

    var promise = relocationInfo.id ?
      birdata.mergeSurveyPoint(sp.id, relocationInfo.id) :
      birdata.updateSurveyPoint(sp.id, relocationInfo);

    self.relocateSurveyPointStatus("updating");
    promise.then(function() {
      self.relocateSurveyPointActive(false);
      self.relocateSurveyPointStatus("idle");

      if(relocationInfo.id) {
        relocationInfo.survey_count += sp.survey_count;
        sp = relocationInfo;
      } else {
        sp.lat = relocationInfo.lat;
        sp.lon = relocationInfo.lon;
      }
      updateSurveyPoint(sp, true);
      self.refreshSurveyPoints(true);
    }).catch(function(error) {
      self.relocateSurveyPointStatus("update-error");
      self.relocateSurveyPointActive(false);
    });
  }

  self.relocateSurveyPointManualEntry = function() {
    var sp = self.survey.surveyPoint();
    resetManualCoordinates();
    self.manualLat(Math.abs(sp.lat).toFixed(8));
    self.manualLon(Math.abs(sp.lon).toFixed(8));
    self.manualLatNS(sp.lat > 0 ? "N" : "S");
    self.manualLonEW(sp.lon > 0 ? "E" : "W");
    self.showDecimalDegrees(true);
    self.showManualCoordinateEntry(true);
  }

  params.parent.currentSection.subscribe(function() {
    self.cancelRelocateSurveyPoint();
    self.cancelRenameSurveyPoint();
  });

  // End survey point relocate

  self.visibleSharedSiteLayers = ko.pureComputed(function() {
    return self.survey.program()?.visibleSharedSiteLayers?.(self.survey) ?? ["birdata"];
  });

  self.showSiteProjectSelector = ko.pureComputed(function() {
    return self.survey.program()?.showSiteProjectSelector ?? false;
  });

  self.projectOptions = ko.pureComputed(function() {
    let options = new Set(self.sharedSites()
      .filter(siteFilterFn(true))
      .map(s => s.metadata?.Project)
      .filter(x => x));
    return Array.from(options).sort();
  });

  self.selectedProject = ko.observable();

  function siteFilterFn(ignoreProject = false) {
    let layers = self.visibleSharedSiteLayers();
    let project = ignoreProject ? null : self.selectedProject();
    return function(site) {
      return !site.inactive && layers.includes(site?.layer) && (project == null || site.metadata?.Project == project);
    }
  }

  self.allAreaLayers = ko.observableArray([]);
  self.selectedAreaLayer = ko.observable();

  birdata.areaLayers().then(function(layers) {
    self.allAreaLayers(_.filter(layers, function(layer) {
      // Exclude RL containment layer
      return layer.code != "rl_containment";
    }));
  });

  self.areaLayers = ko.computed(function() {
    var layers = self.allAreaLayers();
    var program = self.survey.program();

    if(program && program.filterAreaLayers) {
      layers = program.filterAreaLayers(layers);
    }

    if(program && program.defaultAreaLayer) {
      self.selectedAreaLayer(program.defaultAreaLayer(layers));
    } else {
      var ibaLayer = _.find(layers, function(layer) { return layer.code == 'iba'; }); // select IBAs by default
      self.selectedAreaLayer(ibaLayer);
    }

    return layers;
  });


  // Load survey points for user that owns the survey (in case it is being edited by an admin)
  function getSurveyPointsPromise() {
    return params.parent.loadedSurveyJSON ? birdata.surveyPoints({ user_id: params.parent.loadedSurveyJSON.user_id }) : birdata.surveyPoints();
  }

  self.surveyPointsPromise = getSurveyPointsPromise();

  var mapPromise = new Promise(function(resolve, reject) {
    // Plumbing to give us a mapPromise
    self.mapLoadedCallback = resolve;
  });

  // ------- Search

  self.searchPlaceholder = ko.pureComputed(function() {
    if(self.survey.program().code == 'swp') {
      return "Search sites by code (e.g. CAS.017)";
    } else if(self.survey.program().code == 'segbc') {
      return "Enter cell ID (e.g. 56H-0254-6340)";
    } else if(self.siteBasedEntry()) {
      return "Search for site or location";
    } else {
      return "Search for site, location or previous survey point";
    }
  })

  self.searchResultTypes = ko.pureComputed(function() {
    if(self.siteBasedEntry()) {
      return ['SHARED_SITE', 'LOCATION'];
    } else if(self.survey.program().code == 'segbc') {
      return ['GRID_CELL', 'LOCATION']
    } else {
      return null;
    }
  });

  self.searchResultSort = function(result) {
    if(self.siteBasedEntry()) {
      // Shared sites first
      return result.type == 'SHARED_SITE' ? 0 : 1;
    } else {
      // Don't change order
      return 0;
    }
  }

  self.locationResultSelected = function(suggestion) {
    var data = suggestion.data;

    switch(suggestion.type) {
      case 'USER_SURVEY_POINT':
      updateSurveyPoint(data, true);
      break;
      case 'SHARED_SITE':
      mapPromise.then(function(map) {
        map.fitBounds(googleMaps.bounds(data.bounds));
      });
      if(self.siteBasedEntry()) {
        self.siteMenu.setSite(data);
      }
      break;
      case 'LOCATION':
      mapPromise.then(function(map) {
        map.fitBounds(new google.maps.LatLngBounds(
          new google.maps.LatLng(data.ymin, data.xmin),
          new google.maps.LatLng(data.ymax, data.xmax)));
      });
      break;
      case 'GRID_CELL':
      mapPromise.then(function(map) {
        map.setCenter({lat: data.centroid.lat, lng: data.centroid.lon });
        map.setZoom(16);
      });
      break;
    }
  };

  // ------- End search

  var selectedSurveyPointMarker;
  var rippleView;

  mapPromise.then(function(map) {
    rippleView = new googleMaps.RippleView(map);
  });

  function showClickIndicator(latLng) {
    if(rippleView) rippleView.ripple(latLng);
  }

  function wrap(v) {
    return ko.isObservable(v) ? v : ko.observable(v);
  }

  function updateSurveyPoint(surveyPoint, adjustMap) {
    var program = self.survey.program();
    if(program.handleSurveyPointSelection) {
      program.handleSurveyPointSelection(surveyPoint, self.survey);
    }

    if(surveyPoint.id && self.routeBasedEntry()) {
      // We have selected an existing survey point, but this doesn't make sense for route based entry.
      // In theory the only way this can happen is by selecting a survey point from the search bar.
      // In this scenario we should just zoom to the point and do nothing else
      panToPoint(surveyPoint, 14);
      return;
    }

    surveyPoint.name = wrap(surveyPoint.name || "");

    var updateSharedSites = !surveyPoint.sharedSites;

    if(updateSharedSites) {
      surveyPoint.sharedSites = ko.observable(null);
      birdata.sharedSitesAtPoint(surveyPoint.lat, surveyPoint.lon).then(function(sites) {
        surveyPoint.sharedSites(sites);

        // Autofill survey point name if possible
        // Find site in the current visible layers
        let filterFn = siteFilterFn();
        var visibleSitesAtPoint = sites.filter(filterFn);
        var autofillSite = (visibleSitesAtPoint.length == 1) ? visibleSitesAtPoint[0] : null;

        if(autofillSite && !surveyPoint.id && !surveyPoint.name()) {
          surveyPoint.name(autofillSite.name);
        }

        if(self.siteBasedEntry() && !self.siteMenu.selectedSite() && autofillSite) {
          self.siteMenu.setSite(autofillSite);
        }
      });
    }

    surveyPoint.accuracy = ko.observable(self.accuracyOptions[0]);

    setupSurveyPointValidations(surveyPoint);

    self.survey.surveyPoint(surveyPoint);

    if(adjustMap) {
      panToPoint(surveyPoint, 14);
    }
  }

  function panToPoint(latLng, minZoom) {
    mapPromise.then(function(map) {
      if(map.getZoom() >= minZoom) {
        map.panTo(ll(latLng));
      } else {
        map.setCenter(ll(latLng))
        map.setZoom(minZoom);
      }
    });
  }

  ko.computed(function() {
    var map = self.map();
    if(map) {
      var surveyPoint = self.survey.surveyPoint();
      if(surveyPoint && !self.routeBasedEntry()) {
        if(!selectedSurveyPointMarker) {
          selectedSurveyPointMarker = new google.maps.Marker({
            map: map,
            position: ll(surveyPoint)
          });
        } else {
          selectedSurveyPointMarker.setOptions({
            map: map,
            position: ll(surveyPoint)
          });
        }
      } else {
        if(selectedSurveyPointMarker) {
          selectedSurveyPointMarker.setMap(null);
        }
      }
    }
  });

  function setupSurveyPointValidations(sp) {
    if(!sp.id) {
      validate(sp.name, function(name) {
        if(!name.trim()) {
          return "Survey point name is required";
        }
      }, {tab: 'location'});

      validate(sp.accuracy, function(accuracy) {
        if((sp.sharedSites() || []).length == 0 && !accuracy) {
          return "Survey point accuracy is required";
        }
      }, {tab: 'location'});
    }
  }

  function clearSurveyPoint() {
    self.survey.surveyPoint(null);
    if(selectedSurveyPointMarker) {
      selectedSurveyPointMarker.setMap(null);
    }
  }

  function setNewSurveyPoint(latLng) {
    updateSurveyPoint({
      lat: latLng.lat(),
      lon: latLng.lng()
    }, true);
  }

  function handleSurveyPointClick(evt) {
    if(self.relocateSurveyPointActive()) {
      if(this.userData.surveyPoint.id != self.survey.surveyPoint().id) {
        self.selectSurveyPointRelocationDestination(this.userData.surveyPoint);
      }
    } else {
      updateSurveyPoint(this.userData.surveyPoint, true);
    }
  }

  function handleMapClick(evt) {
    if(self.relocateSurveyPointActive()) {
      var coords = { lat: evt.latLng.lat(), lon: evt.latLng.lng() };
      self.selectSurveyPointRelocationDestination(coords);
      return true;
    }
    if(self.routeBasedEntry()) {
      var point = { lat: evt.latLng.lat(), lon: evt.latLng.lng() };
      var map = self.map();
      // if(map.getZoom() < 13) {
      //   map.setCenter(evt.latLng);
      //   map.setZoom(map.getZoom() + 2);
      //   return true;
      // }
      if(self.routeEndpointsRequired()) {
        if(!self.startPoint()) {
          self.startPoint(point);
          return true;
        } else if(!self.finishPoint()) {
          self.finishPoint(point);
          return true;
        }
      }
    } else if(self.siteBasedEntry()) {
      return false;
    } else if(self.canCreateMarkerByTap()) {
      setNewSurveyPoint(evt.latLng);
      return true;
    } else if(self.survey.program().mapGrid) {
      var map = self.map();
      map.setCenter(evt.latLng);
      map.setZoom(16);
      return true;
    }
  }

  // Zoom to shared site

  var pendingZoomSite;
  function zoomToSharedSite(site) {
    if(self.tabVisible() && self.map()) {
      var map = self.map();
      googleMaps.extendAPI();
      var bounds = googleMaps.bounds(site.bounds);
      var mapBounds = map.getBounds();
      var mapBoundsValid = mapBounds && mapBounds.toSpan().lat() != 0;

      if(!mapBoundsValid || !googleMaps.containsBounds(bounds, mapBounds)) {
        bounds = googleMaps.outsetBounds(bounds, 0.0005);
        map.fitBounds(bounds);
      }
    } else {
      pendingZoomSite = site;
    }
  }

  ko.computed(function() {
    if(self.tabVisible() && self.map() && pendingZoomSite) {
      var site = pendingZoomSite;
      pendingZoomSite = undefined;
      // setTimeout is gross but makes zooming more reliable (gives map a chance to be set up properly)
      setTimeout(function() {
        zoomToSharedSite(site);
      });
    }
  });

  function sharedSiteById(id) {
    if(!sharedSiteById.lookup) {
      if(self.sharedSites().length == 0) {
        return undefined;
      }
      sharedSiteById.lookup = {}
      self.sharedSites().forEach(function(site) {
        sharedSiteById.lookup[site.id] = site;
      })
    }
    return sharedSiteById.lookup[id];
  }

  // Map init

  self.initMap = function(element) {
    var myOptions = googleMaps.defaultOptions({
      zoom: 4,
      center: {
        lat: self.lat(),
        lng: self.lng()
      },
      fullscreenControl: false
    });

    var map = new google.maps.Map(element, myOptions);

    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));

    googleMaps.addSingleClickEvents(map);

    google.maps.event.addListener(map, 'center_changed', function() {
      var pos = map.getCenter();
      self.lat(pos.lat());
      self.lng(pos.lng());
    });

    google.maps.event.addListener(map, 'zoom_changed', function() {
      self.zoom(map.getZoom());
    });
    self.zoom(map.getZoom());

    google.maps.event.addListener(map, 'singleclick', handleMapClick);

    google.maps.event.addListener(map, 'firstclick', function(evt) {
      showClickIndicator(evt.latLng);
    });

    // Resize map when becoming visible
    ko.computed(function() {
      if(self.tabVisible()) {
        var center = map.getCenter();
        google.maps.event.trigger(map, 'resize');
        map.setCenter(center);
      }
    });

    var sharedSiteLayer = sharedSitesMap.showLayer(map, {
      layerID: "sharedSites",
      polygonEvents: {
        mousemove: handlePolyMouseMove,
        mouseover: handlePolyMouseOver,
        mouseout: handlePolyMouseOut,
        singleclick: handlePolyClick,
        firstclick: function(evt) { showClickIndicator(evt.latLng); }
      },
      markerEvents: {
        mousemove: handlePolyMouseMove,
        mouseover: handleMarkerMouseOver,
        mouseout: handleMarkerMouseOut,
        click: handleSharedSiteMarkerClick
      },
      sharedSiteLookup: sharedSiteById
    });
    var areaLayer = areasMap.showLayer(map, {
      layerID: "areas",
      polygonEvents: {
        mousemove: handlePolyMouseMove,
        mouseover: handlePolyMouseOver,
        mouseout: handlePolyMouseOut,
        singleclick: handlePolyClick,
        firstclick: function(evt) { showClickIndicator(evt.latLng); }
      }
    });

    ko.computed(function() {
      let filterFn = siteFilterFn();
      sharedSiteLayer.setFeatureFilter(feature => filterFn(feature.userData?.attributes));
    });

    ko.computed(function() {
      var layerID = self.selectedAreaLayer() && self.selectedAreaLayer().id;
      if(layerID) {
        areaLayer.setFeatureFilter(function(feature) {
          var attrs = feature.userData && feature.userData.attributes;
          return !attrs || attrs.layer_id == layerID;
        });
      } else {
        areaLayer.setFeatureFilter(function() { return false; });
      }
    });

    // ------ Site info hover div

    var hoverOverlay = googleMaps.hoverOverlay(map);

    // --- End hover div setup

    function areaById(id) {
      return _.find(self.areas(), function(area) {
        return area.id == id;
      });
    }

    function sharedSiteLabel(site) {
      var title = "Shared site: " + site.name;
      if(site.metadata && site.metadata.fire_status === 'Burnt') {
        title += ' (Fire affected)';
      }
      if(site.inactive) {
        title += ' (Inactive site)';
      }
      return title;
    }

    function hoverContentForPoly(poly) {
      var site, area;

      switch(poly.userData.layerID) {
        case "sharedSites":
        site = sharedSiteById(poly.userData.id);
        if(site) {
          return sharedSiteLabel(site);
        }
        return null;
        case "areas":
        area = areaById(poly.userData.id);
        if(area) {
          return "Area: " + area.name;
        }
        return null;
        default:
        return null;
      }
    }

    function handlePolyMouseOver(evt) {
      var content = hoverContentForPoly(this);

      if(content) {
        hoverOverlay.html(content);
        hoverOverlay.position(evt.latLng);
        hoverOverlay.show();
      } else {
        hoverOverlay.html('');
      }
    }

    function handlePolyMouseOut(evt) {
      hoverOverlay.hide();
    }

    function handlePolyMouseMove(evt) {
      hoverOverlay.position(evt.latLng);
    }

    function handlePolyClick(evt) {
      var site;

      if(handleMapClick(evt)) {
        return;
      }

      switch(this.userData.layerID) {
        case "sharedSites":
          // Zoom to site
          site = sharedSiteById(this.userData.id);
          if(site) {
            zoomToSharedSite(site);
          }
          if(self.routeBasedEntry() && self.routeType() == 'existing') {
            self.selectedRegion(site.metadata.region);
            self.selectedRoute(_.findWhere(self.routes(), { id: site.id }));
          }
          if(self.siteBasedEntry()) {
            self.siteMenu.setSite(site);
          }
      }
    }

    function handleSharedSiteMarkerClick(evt) {
      if(this.userData.sharedSite) {
        zoomToSharedSite(this.userData.sharedSite);
      } else if(this.userData.length) {
        var sharedSitesAtMarker = this.userData.map(x => sharedSiteById(x.id));
        zoomToSharedSite(sharedSitesAtMarker[0]);

        if(self.siteBasedEntry()) {
          if(sharedSitesAtMarker.length == 1) {
            self.selectedSite(sharedSitesAtMarker[0])
          } else {
            self.selectedSite(null)
            setSurveyPointToSiteCentroid(sharedSitesAtMarker[0]);
          }
          self.selectedSiteList(sharedSitesAtMarker);
        }
      }
    }

    function handleMarkerMouseOver(evt) {
      if(this.userData.length) {
        var siteNames = this.userData.map(x => sharedSiteById(x.id)).map(s => s == null ? 'Unknown' : s.name);
        hoverOverlay.html("Shared site: " + siteNames.join(", "));
      } else if(this.userData.surveyPoint) {
        hoverOverlay.html("Survey Point: " + ko.unwrap(this.userData.surveyPoint.name));
      } else if(this.userData.sharedSite) {
        hoverOverlay.html(sharedSiteLabel(this.userData.sharedSite));
      }

      hoverOverlay.position(evt.latLng);
      hoverOverlay.show();
      return true;
    }

    function handleMarkerMouseOut(evt) {
      hoverOverlay.hide();
    }

    // Zoom to shared site if neccesary
    var hasZoomedToSharedSite;
    if(util.hashParams().shared_site_id) {
      birdata.sharedSite(util.hashParams().shared_site_id).then(zoomToSharedSite);
    }

    // Allow programs to manipulate map e.g. to set initial extent
    ko.computed(function() {
      if(self.tabVisible()) {
        self.survey.program()?.updateMap?.(map, self.survey, self.areasPromise, self);
      }
    });

    var surveyPointLayer = markerLayer.layer(map, {
      density: 3,
      listeners: {
        click: handleSurveyPointClick,
        mouseover: handleMarkerMouseOver,
        mouseout: handleMarkerMouseOut,
        mousemove: handlePolyMouseMove
      }
    });
    self.surveyPointLayer = surveyPointLayer;

    ko.computed(function() {
      var program = self.survey.program();
      // We are assuming that a grid is the only kind of overlay map type
      map.overlayMapTypes.clear();
      if(program && program.mapGrid && program.mapGrid.type == 'mga') {
        var gridOverlay = new UTMGridMapType({
          tileSize: new google.maps.Size(512, 512),
          map: map,
          gridSize: program.mapGrid.size || 1000,
          minZoom: 12
        });
        map.overlayMapTypes.push(gridOverlay);
      }
    });

    map.addListener('mousemove', function(evt) {
      var program = self.survey.program();
      if(program.mapGrid && program.mapGrid.type == "mga") {
        var id = utmGrid.gridCellIDFromLatLon(evt.latLng.lat(), evt.latLng.lng(), program.mapGrid.size);
        hoverOverlay.position(evt.latLng);
        hoverOverlay.html(id);
      }
    });



    // Hide survey points if doing route-based entry
    ko.computed(function() {
      var showSurveyPoints = !(self.routeBasedEntry() || self.siteBasedEntry());
      surveyPointLayer.setFilter(function() { return showSurveyPoints; })
    });

    // TODO - extract to utility library
    function observableMarker(position, options, visible) {
      var marker;
      var dragging = false;
      return ko.computed(function() {
        var p = position();

        if(dragging) {
          return;
        }

        if(p && (visible === undefined || ko.unwrap(visible))) {
          var opts = ko.unwrap(options) || {};
          opts.position = ll(p);
          opts.map = map;
          if(marker) {
            marker.setOptions(opts);
          } else {
            marker = new google.maps.Marker(opts);
            google.maps.event.addListener(marker, 'dragstart', function() {
              dragging = true;
            });
            google.maps.event.addListener(marker, 'drag', function() {
              // Uncommenting the following will update the observable as the user drags the marker.
              // This fires off a bunch of continuous web requests which I don't want. It could be worked around by
              // having an 'isDragging' observable and only sending requests when not dragging, but I'd rather not add this
              // complexity. Throttling is also an option but I don't think the UX would be ideal anyway.
              // position({ lat: marker.position.lat(), lon: marker.position.lng() });
            });
            google.maps.event.addListener(marker, 'dragend', function() {
              dragging = false;
              position({ lat: marker.position.lat(), lon: marker.position.lng() });
            });
          }
        } else if(marker) {
          marker.setMap(null);
        }
        return marker;
      });
    }

    // TODO - extract to utility library
    function observableLine(position1, position2, options, visible) {
      var line;
      return ko.computed(function() {
        var p1 = position1(), p2 = position2();
        if(p1 && p2 && (visible === undefined || ko.unwrap(visible))) {
          var opts = ko.unwrap(options) || {};
          opts.path = [ll(p1), ll(p2)];
          opts.map = map;
          if(line) {
            line.setOptions(opts);
          } else {
            line = new google.maps.Polyline(opts);
          }
        } else if(line) {
          line.setMap(null);
        }
      })
    }

    // Route-based stuff
    var startPointMarker = observableMarker(self.startPoint, {
      draggable: true,
      label: "▶"
    }, self.showEndpoints);
    var finishPointMarker = observableMarker(self.finishPoint, {
      draggable: true,
      label: "◼"
    }, self.showEndpoints);
    // observableLine(self.startPoint, self.finishPoint, undefined, self.showEndpoints);

    self.selectedRoute.subscribe(function(r) {
      if(r) {
        zoomToSharedSite(r);
      }
    });

    // Project-selection zoom
    self.selectedProject.subscribe(function(project) {
      let sites = self.sharedSites().filter(siteFilterFn());
      let bounds = new google.maps.LatLngBounds();
      for(let site of sites) {
        bounds.union(googleMaps.bounds(site.bounds));
      }
      map.fitBounds(bounds);
    });

    // Setup sites layer
    self.refreshSurveyPoints = function(refetch) {
      if(refetch) {
        self.surveyPointsPromise = getSurveyPointsPromise();
      }

      self.surveyPointsPromise.then(function(surveyPoints) {
        surveyPointLayer.removeAll();

        var filter = self.survey.program().surveyPointFilter || (sp => !sp.program);
        surveyPoints = _.filter(surveyPoints, filter);

        _.each(surveyPoints, function(surveyPoint) {
          surveyPointLayer.addMarkerOptions({
            position: { lat: surveyPoint.lat, lng: surveyPoint.lon },
            title: surveyPoint.name,
            icon: mapStyles.surveyIcon(false),
            userData: {
              surveyPoint: surveyPoint
            }
          });
        });

        surveyPointLayer.refresh();
      });
    }

    self.refreshSurveyPoints();

    self.survey.program.subscribe(function() {
      self.refreshSurveyPoints();
    });

    // Shared sites point layer
    self.sharedSitesPromise.then(function(sharedSites) {
      var layer = markerLayer.layer(map, {
        density: 2,
        maxZoom: 9,
        listeners: {
          click: handleSharedSiteMarkerClick,
          mouseover: handleMarkerMouseOver,
          mouseout: handleMarkerMouseOut,
          mousemove: handlePolyMouseMove
        }
      });

      var q = jobQueue.queue(500, 10, function(sharedSite) {
        var markerOptions = {
          position: { lat: sharedSite.centroid.lat, lng:sharedSite.centroid.lon },
          title: sharedSite.name,
          userData: {
            sharedSite: sharedSite
          },
          icon: mapStyles.sharedSiteIcon(sharedSite)
        };

        layer.addMarkerOptions(markerOptions);
      }, function() {
        layer.refresh();
      });

      _.each(sharedSites, q.add, q);
      q.finish();

      ko.computed(function() {
        let filterFn = siteFilterFn();
        layer.setFilter(marker => filterFn(marker.markerOptions.userData.sharedSite));
      });
    });

    // Fulfill mapPromise
    self.mapLoadedCallback(map);
    return map;
  };

  // Load survey point for existing survey if necessary
  if(params.parent.loadedSurveyJSON) {
    var json = params.parent.loadedSurveyJSON;
    if(!self.survey.surveyPoint() && json.survey_point) {
      updateSurveyPoint(json.survey_point, true);
    }
    var program = self.survey.program();
    if(program && program.routeBasedEntry) { // maybe later we should put route data in survey.route
      var routeJson = json[program.code];
      if(routeJson.start_lat) {
        self.startPoint({ lat: routeJson.start_lat, lon: routeJson.start_lon });
      }
      if(routeJson.finish_lat) {
        self.finishPoint({ lat: routeJson.finish_lat, lon: routeJson.finish_lon });
      }

      self.completeRouteSurveyed(routeJson.partial_route != null ? !routeJson.partial_route : !routeJson.start_lat);


      if(routeJson.shared_site_id) {
        ko.computed(function() {
          // Make sure everything needed is loaded
          var regions = self.regions();
          var routes = self.sharedSites();
          var map = self.map();

          ko.ignoreDependencies(function() {
            if(regions && routes && routes.length && map) {
              var site = _.findWhere(routes, { id: routeJson.shared_site_id })
              if(site) {
                self.selectedRegion(site.metadata.region);
                self.selectedRoute(site);
              }
            }
          });
        });
      }

      self.routeType(routeJson.shared_site_id ? 'existing' : 'ad-hoc');
    }

    if(self.siteBasedEntry() && json.shared_site_id) {
      self.sharedSitesPromise.then(function(sites) {
        var site = _.findWhere(sites, { id: json.shared_site_id });
        if(site) {
          self.siteMenu.setSite(site);
        }
      });
    }
  }

  self.surveyPointCellID = ko.pureComputed(function() {
    var sp = self.survey.surveyPoint();
    var program = self.survey.program();
    if(sp && program && program.mapGrid) {
      return utmGrid.gridCellIDFromLatLon(
        sp.lat, sp.lon, program.mapGrid.size);
    }
  });
};

ko.components.register('survey-edit-location', {
	viewModel: SurveyLocationViewModel,
	template: require('../../templates/survey-edit-location.html')
});

export default SurveyLocationViewModel;
